import React, { useEffect, useState, useCallback } from 'react';
import './ScientificSimulationWorkspace.css';

import type {
  WorkflowPhase,
  InspectorTab,
  DockTab,
  SswPanelRoute
} from './types';

import { GlobalHeader } from './components/GlobalHeader';
import { WorkflowNavigation } from './components/WorkflowNavigation';
import { ScientificViewportShell } from './components/ScientificViewportShell';
import { InvestigationPanel } from './panels/InvestigationPanel';
import { InspectorPanel } from './panels/InspectorPanel';
import { ContextDock } from './panels/dock/ContextDock';
import { useAppSelector } from '@state/store';

export interface ScientificSimulationWorkspaceProps {
  /**
   * Return navigation bridge: Returns to the Welcome Experience (/).
   */
  onHome: () => void;
}

/**
 * Resolves active panel subroute from browser URL.
 */
function resolveSubrouteFromUrl(): SswPanelRoute {
  if (typeof window === 'undefined') return 'overview';

  const path = window.location.pathname.toLowerCase();
  const hash = window.location.hash.toLowerCase();
  const target = path.includes('/simulation') ? path : hash;

  if (target.includes('/investigation')) return 'investigation';
  if (target.includes('/inspector')) return 'inspector';
  if (target.includes('/validation')) return 'validation';
  if (target.includes('/run-monitor')) return 'run-monitor';
  if (target.includes('/probe-data')) return 'probe-data';
  if (target.includes('/time-series')) return 'time-series';
  if (target.includes('/comparison')) return 'comparison';
  if (target.includes('/console')) return 'console';

  return 'overview';
}

/**
 * SPIN TENET — Scientific Simulation Workspace (SSW)
 * Panel Architecture & Workspace Shell
 *
 * Information Architecture:
 * - Orchestrator shell composing independent panel boundaries.
 * - ZERO scientific state. ZERO CFD configuration. ZERO fake science.
 * - Route-addressable panels:
 *     /simulation
 *     /simulation/investigation
 *     /simulation/inspector
 *     /simulation/validation
 *     /simulation/run-monitor
 *     /simulation/probe-data
 *     /simulation/time-series
 *     /simulation/comparison
 *     /simulation/console
 */
export const ScientificSimulationWorkspace: React.FC<ScientificSimulationWorkspaceProps> = ({ onHome }) => {
  // Navigation & Shell Layout States
  const [currentPhase, setCurrentPhase] = useState<WorkflowPhase>('define');
  const [activeInspectorTab, setActiveInspectorTab] = useState<InspectorTab>('properties');
  const [activeDockTab, setActiveDockTab] = useState<DockTab>('validation');

  const [isInvestigationOpen, setIsInvestigationOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768;
    }
    return true;
  });
  const [isInspectorOpen, setIsInspectorOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 1024;
    }
    return true;
  });
  const [isDockOpen, setIsDockOpen] = useState(true);
  const [isDockExpanded, setIsDockExpanded] = useState(false);

  const [selectedTreeNodeId, setSelectedTreeNodeId] = useState('study-overview');

  const study = useAppSelector((state) => state.study.study);
  const activeScenario = study.scenarios[study.activeScenarioId];
  const selectedGeometryId = useAppSelector((state) => state.geometry.selectedId);

  // When a block is selected in the viewport, ensure right Inspector is open on Properties
  useEffect(() => {
    if (selectedGeometryId) {
      setIsInspectorOpen(true);
      setActiveInspectorTab('properties');
    }
  }, [selectedGeometryId]);

  const handleSelectTreeNode = useCallback((nodeId: string) => {
    setSelectedTreeNodeId(nodeId);
    setIsInspectorOpen(true);
  }, []);

  // Sync route with browser history
  const navigateToSubroute = useCallback((route: SswPanelRoute) => {
    if (typeof window === 'undefined') return;

    const urlPath = route === 'overview' ? '/simulation' : `/simulation/${route}`;
    if (window.location.pathname !== urlPath) {
      window.history.pushState({ panel: route }, '', urlPath);
    }
  }, []);

  // Initialize and listen to browser popstate/hashchange
  useEffect(() => {
    const handleUrlChange = () => {
      const subroute = resolveSubrouteFromUrl();

      if (subroute === 'investigation') {
        setIsInvestigationOpen(true);
      } else if (subroute === 'inspector') {
        setIsInspectorOpen(true);
      } else if (
        subroute === 'validation' ||
        subroute === 'run-monitor' ||
        subroute === 'probe-data' ||
        subroute === 'time-series' ||
        subroute === 'comparison' ||
        subroute === 'console'
      ) {
        setIsDockOpen(true);
        setActiveDockTab(subroute as DockTab);
      }
    };

    handleUrlChange();

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
    };
  }, []);

  const handleDockTabChange = (tab: DockTab) => {
    setActiveDockTab(tab);
    navigateToSubroute(tab);
  };

  const handlePhaseChange = (phase: WorkflowPhase) => {
    setCurrentPhase(phase);
    if (phase === 'run') {
      setIsDockOpen(true);
      setActiveDockTab('run-monitor');
      setSelectedTreeNodeId('run-queue');
    } else if (phase === 'model') {
      setSelectedTreeNodeId('site-wind');
      setIsInspectorOpen(true);
    } else if (phase === 'define') {
      setSelectedTreeNodeId('study-overview');
    }
  };

  const handleToggleInvestigation = () => {
    setIsInvestigationOpen((prev) => {
      const next = !prev;
      if (next) navigateToSubroute('investigation');
      return next;
    });
  };

  const handleToggleInspector = () => {
    setIsInspectorOpen((prev) => {
      const next = !prev;
      if (next) navigateToSubroute('inspector');
      return next;
    });
  };

  return (
    <div
      id="ssw-viewport"
      className="ssw-workspace-shell"
      role="region"
      aria-label="Scientific Simulation Workspace"
    >
      {/* Subtle Static Mineral/Paper Grain Texture Overlay */}
      <svg className="ssw-paper-grain" aria-hidden="true">
        <filter id="ssw-mineral-grain">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.85"
            numOctaves="3"
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#ssw-mineral-grain)" />
      </svg>

      {/* 1. Global Header */}
      <GlobalHeader
        onHome={onHome}
        studyName={study.name}
        scenarioName={activeScenario?.name}
      />

      {/* 2. Workflow Phase Navigation */}
      <WorkflowNavigation
        currentPhase={currentPhase}
        onPhaseChange={handlePhaseChange}
      />

      {/* 3. Core Spatial Layout (Left Panel, Center Viewport, Right Panel, Bottom Dock) */}
      <main className="ssw-spatial-grid" role="main">
        {/* Left: Investigation Panel */}
        <InvestigationPanel
          isOpen={isInvestigationOpen}
          onToggle={handleToggleInvestigation}
          selectedNodeId={selectedTreeNodeId}
          onSelectNode={handleSelectTreeNode}
        />

        {/* Center: Viewport & Bottom Dock Stage */}
        <div className="ssw-center-column">
          {/* Scientific Viewport */}
          <ScientificViewportShell />

          {/* Bottom Context & Analysis Dock */}
          <ContextDock
            activeTab={activeDockTab}
            onTabChange={handleDockTabChange}
            isOpen={isDockOpen}
            onToggleOpen={() => setIsDockOpen((prev) => !prev)}
            isExpanded={isDockExpanded}
            onToggleExpanded={() => setIsDockExpanded((prev) => !prev)}
          />
        </div>

        {/* Right: Inspector Panel */}
        <InspectorPanel
          isOpen={isInspectorOpen}
          onToggle={handleToggleInspector}
          activeTab={activeInspectorTab}
          onTabChange={setActiveInspectorTab}
          selectedNodeId={selectedTreeNodeId}
        />
      </main>

      {/* Mobile-First Utility Bottom Bar for Viewport/Panel Switching */}
      <nav className="ssw-mobile-dock-switcher" aria-label="Mobile Panel Switcher">
        <button
          type="button"
          className={`ssw-mobile-dock-btn ${isInvestigationOpen ? 'is-active' : ''}`}
          onClick={handleToggleInvestigation}
        >
          Investigation
        </button>
        <button
          type="button"
          className={`ssw-mobile-dock-btn ${isDockOpen ? 'is-active' : ''}`}
          onClick={() => setIsDockOpen((prev) => !prev)}
        >
          Dock ({activeDockTab})
        </button>
        <button
          type="button"
          className={`ssw-mobile-dock-btn ${isInspectorOpen ? 'is-active' : ''}`}
          onClick={handleToggleInspector}
        >
          Inspector
        </button>
      </nav>
    </div>
  );
};

export default ScientificSimulationWorkspace;
